/**
 * countries.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

// List of countries for international address handling
export const COUNTRIES = [
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'IE', name: 'Ireland', flag: '🇮🇪' },
  { code: 'SCT', name: 'Scotland', flag: '🏴' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
  { code: 'FR', name: 'France', flag: '🇫🇷' },
  { code: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { code: 'BE', name: 'Belgium', flag: '🇧🇪' },
  { code: 'CH', name: 'Switzerland', flag: '🇨🇭' },
  { code: 'AT', name: 'Austria', flag: '🇦🇹' },
  { code: 'DK', name: 'Denmark', flag: '🇩🇰' },
  { code: 'SE', name: 'Sweden', flag: '🇸🇪' },
  { code: 'NO', name: 'Norway', flag: '🇳🇴' },
  { code: 'FI', name: 'Finland', flag: '🇫🇮' },
  { code: 'PL', name: 'Poland', flag: '🇵🇱' },
  { code: 'CZ', name: 'Czech Republic', flag: '🇨🇿' },
  { code: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷' },
  { code: 'CN', name: 'China', flag: '🇨🇳' },
  { code: 'IN', name: 'India', flag: '🇮🇳' },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷' },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽' },
  { code: 'AR', name: 'Argentina', flag: '🇦🇷' },
  { code: 'ZA', name: 'South Africa', flag: '🇿🇦' },
];

/** Shown first in international Country Name dropdown */
export const INTL_COUNTRY_PRIORITY = [
  { code: 'CA', name: 'Canada' },
  { code: 'US', name: 'United States' },
  { code: 'IE', name: 'Ireland' },
  { code: 'SCT', name: 'Scotland' },
  { code: 'GB', name: 'United Kingdom' },
];

export const OTHER_COUNTRY_VALUE = '__OTHER__';

const PRIORITY_NAME_KEYS = new Set(
  INTL_COUNTRY_PRIORITY.map((c) => c.name.toLowerCase())
);

/** International country options: priority destinations first, then the rest A–Z */
export function getInternationalCountryOptions() {
  const rest = COUNTRIES
    .filter((c) => !PRIORITY_NAME_KEYS.has(c.name.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));
  return [...INTL_COUNTRY_PRIORITY, ...rest];
}

export function isKnownInternationalCountry(name = '') {
  const key = String(name || '').trim().toLowerCase();
  if (!key) return false;
  return getInternationalCountryOptions().some((c) => c.name.toLowerCase() === key);
}

/** If a priority pick maps to CA/US, return that code so the main Country dropdown can switch */
export function countryNameToDomesticCode(name = '') {
  const key = String(name || '').trim().toLowerCase();
  if (key === 'canada' || key === 'ca') return 'CA';
  if (key === 'united states' || key === 'usa' || key === 'us') return 'US';
  return null;
}

// Canadian provinces/territories
export const CANADIAN_PROVINCES = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' },
];

// US states
export const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

// Get postal code label based on country
export const getPostalCodeLabel = (countryCode) => {
  const labels = {
    'US': 'ZIP Code',
    'CA': 'Postal Code',
    'GB': 'Postcode',
    'AU': 'Postcode',
    'NZ': 'Postcode',
    'IE': 'Eircode',
  };
  return labels[countryCode] || 'Postal Code';
};

// Get postal code placeholder based on country
export const getPostalCodePlaceholder = (countryCode) => {
  const placeholders = {
    'US': '12345',
    'CA': 'A1B 2C3',
    'GB': 'SW1A 1AA',
    'AU': '2000',
    'NZ': '1010',
    'IE': 'D01 F5P2',
  };
  return placeholders[countryCode] || 'Enter postal code';
};

// Get province/state label based on country
export const getProvinceLabel = (countryCode) => {
  const labels = {
    'US': 'State',
    'CA': 'Province',
    'GB': 'County',
    'AU': 'State',
    'IE': 'County',
  };
  return labels[countryCode] || 'Province/State';
};

// Get provinces/states for a country
export const getProvinces = (countryCode) => {
  switch (countryCode) {
    case 'CA':
      return CANADIAN_PROVINCES;
    case 'US':
      return US_STATES;
    default:
      return null; // Free text input for other countries
  }
};

// Validate postal code format
export const validatePostalCode = (postalCode, countryCode) => {
  if (!postalCode) return true; // Optional field
  
  const patterns = {
    'CA': /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/,
    'US': /^\d{5}(-\d{4})?$/,
    'GB': /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i,
    'AU': /^\d{4}$/,
    'NZ': /^\d{4}$/,
  };
  
  const pattern = patterns[countryCode];
  return !pattern || pattern.test(postalCode);
};

